import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@material-ui/core';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import String from '../components/String';
import Layout from '../components/Layout';
import SetupStep from '../components/SetupStep';
import { useLanguage } from '../contexts/language';
import { useQueryParams } from '../hooks/useQueryParams';
import LanguageSelector from '../components/LanguageSelector';
import { getLandingPageRedirect } from '../utils/supabase';
import { isSafeRedirectUrl } from '../utils/utils';
import { removeManualCodeLibrarySuffix } from '../utils/manualCode';

// Watchdog so a hanging edge-function call doesn't keep the user on a blank
// loader forever — fall back to the instructions page after this much time.
const REDIRECT_TIMEOUT_MS = 3000;

const ManualCodeInstructions = () => {
  const { content } = useLanguage();
  const queryParams = useQueryParams();
  const codeFromUrl = queryParams.get('agami-manual-code') ?? '';
  const decodedCodeFromUrl = decodeURIComponent(codeFromUrl);
  // Library spots carry a §lib_<uuid> suffix that the manual-code field never
  // expects: show the code the user actually has to type.
  const displayCode = removeManualCodeLibrarySuffix(decodedCodeFromUrl) ?? '';
  const landingPageCode = queryParams.get('landing-page') ?? '';
  const decodedLandingPageCode = decodeURIComponent(landingPageCode);

  const [loading, setLoading] = useState(!!decodedLandingPageCode);

  useEffect(() => {
    if (!decodedLandingPageCode) return undefined;

    let settled = false;
    const to = setTimeout(() => {
      if (settled) return;
      settled = true;
      setLoading(false);
    }, REDIRECT_TIMEOUT_MS);

    const redirectToLandingPage = async (code) => {
      const { data, error } = await getLandingPageRedirect({ code });
      if (settled) return;
      settled = true;
      clearTimeout(to);
      if (!error && isSafeRedirectUrl(data?.url)) {
        window.location.replace(data.url);
        return;
      }
      setLoading(false);
    };
    redirectToLandingPage(decodedLandingPageCode);

    return () => {
      settled = true;
      clearTimeout(to);
    };
  }, [decodedLandingPageCode]);

  if (loading) {
    return (
      <Layout fullscreen framed>
        <Box display="flex" justifyContent="center" alignItems="center" mt={6}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout
      fullscreen
      framed
      top={
        <Box mt="8vh" textAlign="center">
          <Box position="absolute" top={0} right={0} textAlign="right">
            <LanguageSelector />
          </Box>
          <Logo />
        </Box>
      }
    >
      <SetupStep
        themeSwitcher
        title={content('manual_code_intro')}
        form={
          <>
            {/* Body */}
            <Stack mt={4} spacing={2} alignItems="center" aria-labelledby="manual-code-explanation">
              <Typography
                component="p"
                variant="body1"
                align="center"
                aria-label={content('aria_explanation')}
                id="manual-code-explanation"
              >
                <strong>
                  <String s="manual_code_heading" />
                </strong>
              </Typography>

              <Typography component="p" variant="body1" align="center">
                <String s="manual_code_explanation" />
              </Typography>

              {displayCode && (
                <Box
                  mt={1}
                  px={2}
                  py={1}
                  borderRadius={8}
                  border="1px solid rgba(0,0,0,0.12)"
                  aria-live="polite"
                >
                  <Typography component="p" variant="caption" align="center">
                    <em>
                      <String s="manual_code_label" />:
                    </em>{' '}
                    <strong>{displayCode}</strong>
                  </Typography>
                </Box>
              )}

              {/* The scanner is not a top-level feature: spell out the path to it,
                  naming the labels the user will actually see in the app. */}
              <Stack component="ol" spacing={1} sx={{ pl: 3, m: 0, textAlign: 'left' }}>
                {['manual_code_step_1', 'manual_code_step_2', 'manual_code_step_3', 'manual_code_step_4'].map(
                  (step) => (
                    <Typography key={step} component="li" variant="body1">
                      <String s={step} />
                    </Typography>
                  )
                )}
              </Stack>

              {/* The QR button only exists when the experience has qr_trigger on:
                  typing the code is the fallback for everyone else. */}
              <Typography component="p" variant="caption" align="center" color="textSecondary">
                <String s="manual_code_qr_hint" />
              </Typography>

              {/* CTAs */}
              <Stack
                spacing={1.5}
                direction="column"
                alignItems="stretch"
                mt={2}
                sx={{ width: '100%', maxWidth: 420 }}
              >
                {/* "/" redirects to /setup/1 (or Login when unauthenticated):
                    the entry point of the flow described in the steps above. */}
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  component={Link}
                  to="/"
                  aria-label={content('aria_button_continue')}
                >
                  <String s="manual_code_cta_continue" />
                </Button>
              </Stack>
            </Stack>

            {/* Footer */}
            <Box mt={4}>
              <Typography component="p" variant="caption" align="center" color="textSecondary">
                <String s="manual_code_footer" />{' '}
                <a href="https://agami.mezzoforte.design" target="_blank" rel="noreferrer">
                  agami.mezzoforte.design
                </a>
              </Typography>
            </Box>
          </>
        }
      />
    </Layout>
  );
};

export default ManualCodeInstructions;
